import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CircleCheck, Loader as LoaderIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api, getUTM } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ComplianceNote } from "@/components/shared/Shared";

// Per-flow field configuration. Each field: {key, type, label(t), options?}
export function useFormConfigs() {
  const { t } = useLang();
  return {
    nettopolice: {
      title: t("Nettopolicen-Beratung anfragen", "Request Net Policy Consultation"),
      intro: t(
        "Wir pr\u00fcfen individuell, ob eine Nettopolice zu Ihrer Vorsorgestrategie passt \u2013 transparent und ohne Produktdruck.",
        "We individually assess whether a net policy fits your provision strategy \u2013 transparently and without product pressure."
      ),
      fields: [
        { key: "ziel", type: "select", label: t("Ihr prim\u00e4res Ziel", "Your primary goal"), options: [
          ["altersvorsorge", t("Altersvorsorge", "Retirement provision")],
          ["vermoegensaufbau", t("Verm\u00f6gensaufbau", "Wealth accumulation")],
          ["beides", t("Beides", "Both")],
        ]},
        { key: "horizont", type: "select", label: t("Anlagehorizont", "Investment horizon"), options: [
          ["unter_10", t("Unter 10 Jahre", "Under 10 years")],
          ["10_20", t("10\u201320 Jahre", "10\u201320 years")],
          ["ueber_20", t("\u00dcber 20 Jahre", "Over 20 years")],
        ]},
        { key: "sparrate", type: "select", label: t("Gedachte monatliche Sparrate", "Intended monthly savings rate"), options: [
          ["bis_250", t("Bis 250 \u20ac", "Up to \u20ac250")],
          ["250_500", "250\u2013500 \u20ac"],
          ["500_1000", "500\u20131.000 \u20ac"],
          ["ueber_1000", t("\u00dcber 1.000 \u20ac", "Over \u20ac1,000")],
        ]},
      ],
    },
    vertragspruefung: {
      title: t("Bestehende Vertr\u00e4ge pr\u00fcfen lassen", "Have Existing Contracts Reviewed"),
      intro: t(
        "Drei Angaben gen\u00fcgen: Tarif, R\u00fcckkaufswert und Sparrate. Sie erhalten in der Regel innerhalb von 48 Stunden ein dokumentiertes Ergebnis \u2013 ohne Vorabkosten und ohne vorschnelle K\u00fcndigung.",
        "Three details are enough: tariff, surrender value and savings rate. You will usually receive a documented result within 48 hours \u2013 no upfront costs, no premature cancellation."
      ),
      fields: [
        { key: "vertragsart", type: "select", label: t("Art des Vertrags", "Type of contract"), options: [
          ["lebensversicherung", t("Lebens-/Rentenversicherung", "Life/pension insurance")],
          ["fondspolice", t("Fondsgebundene Police", "Unit-linked policy")],
          ["riester_ruerup", t("Riester / R\u00fcrup", "Riester / R\u00fcrup")],
          ["sonstiges", t("Sonstiges / unsicher", "Other / unsure")],
        ]},
        { key: "anzahl", type: "select", label: t("Anzahl der Vertr\u00e4ge", "Number of contracts"), options: [
          ["1", "1"], ["2_3", "2\u20133"], ["mehr", t("Mehr als 3", "More than 3")],
        ]},
      ],
    },
    immobilien: {
      title: t("Kostenfreier Finanzierungscheck", "Free Financing Check"),
      intro: t(
        "Eleven Real Estate: Wir pr\u00fcfen unverbindlich, welcher Investitionsrahmen f\u00fcr eine vermietete Bestandswohnung zu Ihrer Situation passt \u2013 R\u00fcckmeldung in der Regel innerhalb von 48 Stunden.",
        "Eleven Real Estate: We check, without obligation, which investment range for a tenanted existing apartment suits your situation \u2013 feedback usually within 48 hours."
      ),
      fields: [
        { key: "zweck", type: "select", label: t("Zielsetzung", "Objective"), options: [
          ["kapitalanlage", t("Kapitalanlage", "Capital investment")],
          ["bestand", t("Bestandsoptimierung", "Portfolio optimization")],
          ["unsicher", t("Noch offen / Beratung gew\u00fcnscht", "Still open / advice desired")],
        ]},
        { key: "budget", type: "select", label: t("Investitionsrahmen", "Investment range"), options: [
          ["bis_300k", t("Bis 300.000 \u20ac", "Up to \u20ac300,000")],
          ["300_600k", "300.000\u2013600.000 \u20ac"],
          ["ueber_600k", t("\u00dcber 600.000 \u20ac", "Over \u20ac600,000")],
        ]},
        { key: "eigenkapital", type: "select", label: t("Verf\u00fcgbares Eigenkapital", "Available equity"), options: [
          ["ohne", t("Ohne / m\u00f6glichst wenig", "None / as little as possible")],
          ["bis_50k", t("Bis 50.000 \u20ac", "Up to \u20ac50,000")],
          ["ueber_50k", t("\u00dcber 50.000 \u20ac", "Over \u20ac50,000")],
        ]},
      ],
    },
    festgeld: {
      title: t("Festgeld-Verf\u00fcgbarkeit pr\u00fcfen", "Check Fixed-Term Deposit Availability"),
      intro: t(
        "Konditionen werden situationsbezogen und erst nach individueller Pr\u00fcfung der Verf\u00fcgbarkeit kommuniziert. Ihre Anfrage ist unverbindlich.",
        "Conditions are communicated situationally and only after individual availability checks. Your inquiry is non-binding."
      ),
      fields: [
        { key: "volumen", type: "select", label: t("Gedachtes Anlagevolumen", "Intended investment volume"), options: [
          ["25_50k", "25.000\u201350.000 \u20ac"],
          ["50_100k", "50.000\u2013100.000 \u20ac"],
          ["100_250k", "100.000\u2013250.000 \u20ac"],
          ["ueber_250k", t("\u00dcber 250.000 \u20ac", "Over \u20ac250,000")],
        ]},
        { key: "laufzeit", type: "select", label: t("Bevorzugte Laufzeit", "Preferred term"), options: [
          ["6_12", t("6\u201312 Monate", "6\u201312 months")],
          ["12_24", t("12\u201324 Monate", "12\u201324 months")],
          ["flexibel", t("Flexibel", "Flexible")],
        ]},
        { key: "zeitrahmen", type: "select", label: t("Anlagezeitpunkt", "Investment timing"), options: [
          ["sofort", t("Kurzfristig verf\u00fcgbar", "Available short-term")],
          ["3_monate", t("Innerhalb von 3 Monaten", "Within 3 months")],
          ["offen", t("Noch offen", "Still open")],
        ]},
      ],
    },
    beraterbewerbung: {
      title: t("Als Berater bewerben", "Apply as Advisor"),
      intro: t(
        "Werden Sie Teil einer modernen Beratungsplattform \u2013 mit digitalen Prozessen, Academy und professionellem Leadmanagement.",
        "Become part of a modern advisory platform \u2013 with digital processes, academy and professional lead management."
      ),
      fields: [
        { key: "erfahrung", type: "select", label: t("Erfahrung in der Finanzberatung", "Experience in financial advisory"), options: [
          ["keine", t("Keine \u2013 Quereinstieg", "None \u2013 career change")],
          ["1_3", t("1\u20133 Jahre", "1\u20133 years")],
          ["3_10", t("3\u201310 Jahre", "3\u201310 years")],
          ["ueber_10", t("\u00dcber 10 Jahre", "Over 10 years")],
        ]},
        { key: "region", type: "text", label: t("Region / Standort", "Region / location") },
        { key: "motivation", type: "textarea", label: t("Was motiviert Sie?", "What motivates you?") },
      ],
    },
    partner: {
      title: t("Partner werden", "Become a Partner"),
      intro: t(
        "Kooperationen mit Produktgebern, Kanzleien und Unternehmen \u2013 strukturiert, transparent und langfristig.",
        "Cooperations with product providers, firms and companies \u2013 structured, transparent and long-term."
      ),
      fields: [
        { key: "unternehmen", type: "text", label: t("Unternehmen", "Company") },
        { key: "partnerschaft", type: "select", label: t("Art der Partnerschaft", "Type of partnership"), options: [
          ["produktgeber", t("Produktgeber", "Product provider")],
          ["kooperation", t("Strategische Kooperation", "Strategic cooperation")],
          ["empfehlung", t("Empfehlungspartner", "Referral partner")],
          ["sonstiges", t("Sonstiges", "Other")],
        ]},
      ],
    },
    rueckruf: {
      title: t("R\u00fcckruf anfordern", "Request a Callback"),
      intro: t(
        "Wir melden uns pers\u00f6nlich bei Ihnen \u2013 diskret und zu Ihrem Wunschzeitraum.",
        "We will contact you personally \u2013 discreetly and at your preferred time."
      ),
      fields: [
        { key: "thema", type: "select", label: t("Ihr Thema", "Your topic"), options: [
          ["family_office", "Family Office"],
          ["immobilien", t("Real Estate / Immobilien", "Real estate")],
          ["vorsorge", t("Maklerservice / Vorsorge", "Brokerage / provision")],
          ["festgeld", t("Festgeld", "Fixed-term deposits")],
          ["allgemein", t("Allgemeine Beratung", "General consultation")],
        ]},
        { key: "callback_window", type: "select", label: t("Bevorzugter R\u00fcckrufzeitraum", "Preferred callback window"), options: [
          ["vormittags", t("Vormittags (9\u201312 Uhr)", "Morning (9am\u201312pm)")],
          ["nachmittags", t("Nachmittags (12\u201317 Uhr)", "Afternoon (12\u20135pm)")],
          ["abends", t("Abends (17\u201319 Uhr)", "Evening (5\u20137pm)")],
        ]},
      ],
    },
    erstgespraech: {
      title: t("Pers\u00f6nliches Erstgespr\u00e4ch vereinbaren", "Book a Personal Initial Consultation"),
      intro: t(
        "Kostenfrei und unverbindlich: Wir h\u00f6ren zu, verstehen Ihre Situation und zeigen Ihnen, welcher Bereich der II Eleven Gruppe f\u00fcr Sie der richtige Einstieg ist.",
        "Free and non-binding: We listen, understand your situation and show you which area of the II Eleven Group is the right starting point for you."
      ),
      fields: [
        { key: "thema", type: "select", label: t("Worum geht es?", "What is it about?"), options: [
          ["family_office", t("Family Office \u2013 Verm\u00f6gensstruktur & Verwaltung", "Family Office \u2013 wealth structure & management")],
          ["real_estate", t("Real Estate \u2013 Kapitalanlage-Immobilie", "Real Estate \u2013 investment property")],
          ["maklerservice", t("Maklerservice \u2013 Vorsorge & Vertr\u00e4ge", "Brokerage \u2013 provision & contracts")],
          ["development", t("Development \u2013 Sanierung & Betreuung", "Development \u2013 renovation & management")],
          ["allgemein", t("Noch offen / Allgemein", "Still open / general")],
        ]},
        { key: "callback_window", type: "select", label: t("Bevorzugter Zeitraum", "Preferred time window"), options: [
          ["vormittags", t("Vormittags (9\u201312 Uhr)", "Morning (9am\u201312pm)")],
          ["nachmittags", t("Nachmittags (12\u201317 Uhr)", "Afternoon (12\u20135pm)")],
          ["abends", t("Abends (17\u201319 Uhr)", "Evening (5\u20137pm)")],
        ]},
      ],
    },
    strukturanalyse: {
      title: t("Strukturanalyse anfragen", "Request a Structural Analysis"),
      intro: t(
        "Der Einstieg ins Family Office: In rund 45 Minuten analysieren wir Ihre bestehende Verm\u00f6gensstruktur \u2013 kostenfrei, diskret und dokumentiert. Ihr Kapital flie\u00dft zu keinem Zeitpunkt an Eleven.",
        "Your entry to the Family Office: In around 45 minutes we analyze your existing wealth structure \u2013 free of charge, discreet and documented. Your capital is never transferred to Eleven."
      ),
      fields: [
        { key: "anliegen", type: "select", label: t("Ihr prim\u00e4res Anliegen", "Your primary concern"), options: [
          ["struktur", t("Internationale Verm\u00f6gensstruktur", "International wealth structure")],
          ["verwaltung", t("Verm\u00f6gensverwaltung", "Wealth management")],
          ["schutz", t("Verm\u00f6gensschutz & Nachfolge", "Wealth protection & succession")],
          ["gesamt", t("Gesamtbild / noch offen", "Overall picture / still open")],
        ]},
        { key: "horizont", type: "select", label: t("Anlagehorizont", "Investment horizon"), options: [
          ["unter_3", t("Unter 3 Jahre", "Under 3 years")],
          ["3_10", t("3\u201310 Jahre", "3\u201310 years")],
          ["ueber_10", t("\u00dcber 10 Jahre / generationen\u00fcbergreifend", "Over 10 years / multi-generational")],
        ]},
        { key: "beratung_bisher", type: "select", label: t("Arbeiten Sie bereits mit Beratern?", "Do you already work with advisors?"), options: [
          ["steuerberater", t("Ja, Steuerberater", "Yes, tax advisor")],
          ["vermoegensverwalter", t("Ja, Verm\u00f6gensverwalter / Bank", "Yes, asset manager / bank")],
          ["beides", t("Beides", "Both")],
          ["nein", t("Nein", "No")],
        ]},
      ],
    },
  };
}

export default function MultiStepForm({ formType }) {
  const { t, tt, lang } = useLang();
  const configs = useFormConfigs();
  const fallbackConfig = configs[formType];

  // Dynamic form definition from CMS (admin-editable). Falls back to the
  // hardcoded config if the API is unavailable.
  const [definition, setDefinition] = useState(null);
  useEffect(() => {
    let mounted = true;
    api
      .get(`/public/form-definitions/${formType}`)
      .then((r) => mounted && setDefinition(r.data))
      .catch(() => mounted && setDefinition(null));
    return () => {
      mounted = false;
    };
  }, [formType]);

  const config = useMemo(() => {
    if (definition) {
      return {
        title: tt(definition.title),
        intro: tt(definition.intro),
        fields: (definition.fields || []).map((f) => ({
          key: f.key,
          type: f.type,
          required: f.required !== false,
          label: tt(f.label),
          options: (f.options || []).map((o) => [o.value, tt(o.label) || o.value]),
        })),
      };
    }
    return fallbackConfig;
  }, [definition, fallbackConfig, tt]);

  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState({ privacy: false, contact: false, marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const totalSteps = 3;
  const progress = success ? 100 : ((step + 1) / totalSteps) * 100;

  const fieldCls = "h-11 rounded-[6px]";

  const set = (key, value) => setData((d) => ({ ...d, [key]: value }));

  if (!config) {
    return (
      <Card className="rounded-[8px] border-border">
        <CardContent className="py-10 text-center text-muted-foreground">
          {t("Unbekannter Anfragetyp.", "Unknown request type.")}{" "}
          <Link to="/privatkunden" className="underline text-[#111318]">{t("Zur\u00fcck", "Back")}</Link>
        </CardContent>
      </Card>
    );
  }

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      config.fields.forEach((f) => {
        if (f.required === false) return;
        if (!data[f.key] || String(data[f.key]).trim() === "") errs[f.key] = true;
      });
    }
    if (step === 1) {
      if (!contact.first_name.trim()) errs.first_name = true;
      if (!contact.last_name.trim()) errs.last_name = true;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) errs.email = true;
    }
    if (step === 2 && !consent.privacy) errs.privacy = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { callback_window, ...extra } = data;
      await api.post(`/public/forms/${formType}`, {
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        phone: contact.phone || null,
        message: contact.message || null,
        callback_window: callback_window || null,
        product_interest: formType,
        consent,
        utm: getUTM(),
        locale: lang,
        extra,
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(t("Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.", "Request could not be sent. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="lead-form-success">
        <Card className="rounded-[8px] border-border">
          <CardContent className="py-12 px-6 sm:px-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#FDEFEC] border border-[#FF3C20]/40 flex items-center justify-center">
              <CircleCheck className="h-7 w-7 text-[#E8241B]" />
            </div>
            <h2 className="mt-6 font-display text-2xl sm:text-3xl font-semibold text-[#111318]" data-testid="lead-form-success-title">
              {t("Vielen Dank f\u00fcr Ihre Anfrage", "Thank you for your request")}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
              {t(
                "Ihre Anfrage wurde sicher \u00fcbermittelt. Ein zust\u00e4ndiger Ansprechpartner meldet sich in der Regel innerhalb eines Werktages bei Ihnen.",
                "Your request has been securely submitted. A responsible contact person will usually get back to you within one business day."
              )}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild className="rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="success-book-appointment">
                <Link to="/termin">{t("Direkt Termin buchen", "Book appointment directly")}</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-[6px] hover:border-[#FF3C20]/60" data-testid="success-back-home">
                <Link to="/">{t("Zur Startseite", "Back to home")}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <Card className="rounded-[8px] border-border" data-testid={`lead-form-${formType}`}>
      <CardContent className="p-6 sm:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{t("Schritt", "Step")} {step + 1} / {totalSteps}</span>
            <span className="font-mono-num">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1.5 bg-[#ECEEF1] [&>div]:bg-[#111318]" />
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}>
            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#111318]">{config.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{config.intro}</p>
                </div>
                {config.fields.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-sm font-medium">{f.label}{f.required === false ? "" : " *"}</Label>
                    {f.type === "select" && (
                      <Select value={data[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                        <SelectTrigger className={`${fieldCls} ${errors[f.key] ? "border-destructive" : ""}`} data-testid={`form-field-${f.key}`}>
                          <SelectValue placeholder={t("Bitte w\u00e4hlen", "Please select")} />
                        </SelectTrigger>
                        <SelectContent>
                          {f.options.map(([val, label]) => (
                            <SelectItem key={val} value={val} data-testid={`form-option-${f.key}-${val}`}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {f.type === "text" && (
                      <Input value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className={`${fieldCls} ${errors[f.key] ? "border-destructive" : ""}`} data-testid={`form-field-${f.key}`} />
                    )}
                    {f.type === "textarea" && (
                      <Textarea value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={4} className={`rounded-[6px] ${errors[f.key] ? "border-destructive" : ""}`} data-testid={`form-field-${f.key}`} />
                    )}
                    {errors[f.key] && <p className="text-xs text-destructive">{t("Bitte ausf\u00fcllen", "Please fill in")}</p>}
                  </div>
                ))}
              </div>
            )}
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#111318]">{t("Ihre Kontaktdaten", "Your contact details")}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{t("Damit wir Sie pers\u00f6nlich erreichen k\u00f6nnen.", "So we can reach you personally.")}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>{t("Vorname", "First name")} *</Label>
                    <Input value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} className={`${fieldCls} ${errors.first_name ? "border-destructive" : ""}`} data-testid="form-first-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("Nachname", "Last name")} *</Label>
                    <Input value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} className={`${fieldCls} ${errors.last_name ? "border-destructive" : ""}`} data-testid="form-last-name" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>E-Mail *</Label>
                  <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={`${fieldCls} ${errors.email ? "border-destructive" : ""}`} data-testid="form-email" />
                  {errors.email && <p className="text-xs text-destructive">{t("Bitte g\u00fcltige E-Mail angeben", "Please enter a valid email")}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Telefon (optional)", "Phone (optional)")}</Label>
                  <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={fieldCls} data-testid="form-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Ihre Nachricht (optional)", "Your message (optional)")}</Label>
                  <Textarea rows={3} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="rounded-[6px]" data-testid="form-message" />
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#111318]">{t("Einwilligung & Absenden", "Consent & Submit")}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{t("Transparenz ist Teil unseres Selbstverst\u00e4ndnisses.", "Transparency is part of who we are.")}</p>
                </div>
                <div className="space-y-4">
                  <label className={`flex items-start gap-3 rounded-[6px] border p-4 cursor-pointer ${errors.privacy ? "border-destructive" : "border-border"}`}>
                    <Checkbox checked={consent.privacy} onCheckedChange={(v) => setConsent({ ...consent, privacy: !!v })} className="mt-0.5" data-testid="lead-form-consent-checkbox" />
                    <span className="text-sm leading-relaxed text-[#3B4452]">
                      {t("Ich habe die", "I have read the")}{" "}
                      <Link to="/datenschutz" target="_blank" className="underline underline-offset-2 text-[#111318]">{t("Datenschutzerkl\u00e4rung", "privacy policy")}</Link>{" "}
                      {t("gelesen und willige in die Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage ein. *", "and consent to the processing of my data to handle my request. *")}
                    </span>
                  </label>
                  {errors.privacy && <p className="text-xs text-destructive">{t("Diese Einwilligung ist erforderlich", "This consent is required")}</p>}
                  <label className="flex items-start gap-3 rounded-[6px] border border-border p-4 cursor-pointer">
                    <Checkbox checked={consent.contact} onCheckedChange={(v) => setConsent({ ...consent, contact: !!v })} className="mt-0.5" data-testid="form-consent-contact" />
                    <span className="text-sm leading-relaxed text-[#3B4452]">{t("Ich bin mit einer Kontaktaufnahme per Telefon oder E-Mail einverstanden.", "I agree to be contacted by phone or email.")}</span>
                  </label>
                  <label className="flex items-start gap-3 rounded-[6px] border border-border p-4 cursor-pointer">
                    <Checkbox checked={consent.marketing} onCheckedChange={(v) => setConsent({ ...consent, marketing: !!v })} className="mt-0.5" data-testid="form-consent-marketing" />
                    <span className="text-sm leading-relaxed text-[#3B4452]">{t("Ich m\u00f6chte gelegentlich relevante Informationen erhalten (optional, jederzeit widerrufbar).", "I would like to occasionally receive relevant information (optional, revocable at any time).")}</span>
                  </label>
                </div>
                <ComplianceNote>
                  {t(
                    "Ihre Anfrage stellt keine Anlageberatung dar. Alle Empfehlungen erfolgen erst nach individueller Pr\u00fcfung Ihrer Situation.",
                    "Your request does not constitute investment advice. All recommendations are made only after individual assessment of your situation."
                  )}
                </ComplianceNote>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex items-center justify-between">
          {step > 0 ? (
            <Button variant="ghost" className="rounded-[6px] text-[#6B7280] hover:text-[#111318]" onClick={() => setStep((s) => s - 1)} data-testid="lead-form-back-button">
              <ArrowLeft className="mr-1 h-4 w-4" /> {t("Zur\u00fcck", "Back")}
            </Button>
          ) : (
            <span />
          )}
          {step < 2 ? (
            <Button className="rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold active:scale-95" onClick={next} data-testid="lead-form-next-button">
              {t("Weiter", "Next")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button className="rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold active:scale-95" onClick={submit} disabled={submitting} data-testid="lead-form-submit-button">
              {submitting ? (
                <>
                  <LoaderIcon className="mr-2 h-4 w-4 animate-spin" /> {t("Wird gesendet\u2026", "Sending\u2026")}
                </>
              ) : (
                t("Anfrage absenden", "Submit request")
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
